import React, { useCallback, type ReactElement } from 'react';
import { graphql, useFragment } from 'react-relay';
import {
	ExpandableMenuItemContent,
	ExpandableMenuItemTrigger,
} from '@atlaskit/side-nav-items/expandable-menu-item';
import { SECTION_ITEM_PLANS } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/section-type.tsx';
import { MENU_ID_PLANS } from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import {
	L1_MENU_ID,
	type Nav4DragAndDropMenuItemProps,
} from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { Nav4ExpandableMenuItem } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/nav4-expandable-menu-item/index.tsx';
import { getTestId } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/get-test-id/index.tsx';
import { useSectionItemAnalytics } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/use-section-item-analytics/index.tsx';
import { useConfigurableMessageAndIconMap } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/controllers/use-configurable-message-and-icon-map/index.tsx';
// eslint-disable-next-line jira/import/file-extension-required
import { componentWithFG } from '@jira/platform__utils-feature-gates-gate-component';
import { Nav4PlansFooter } from '../common/footer/Nav4PlansFooter.tsx';
import { PlansList } from '../common/plans-list/PlansList.tsx';
import type {
	Nav4PlansForLanding$key,
	Nav4PlansForLanding$data,
} from './__generated__/Nav4PlansForLanding.graphql';

type Nav4PlansForLandingProps = Nav4DragAndDropMenuItemProps & {
	actionsOnHover: ReactElement;
	isExpanded: boolean;
	queryRef: Nav4PlansForLanding$key;
};

export function Nav4PlansForLanding(props: Nav4PlansForLandingProps): React.JSX.Element {
	const { queryRef } = props;
	const { formattedMessage, icon } = useConfigurableMessageAndIconMap(L1_MENU_ID.PLANS);
	const sendSectionItemAnalyticsEvent = useSectionItemAnalytics();

	// eslint-disable-next-line @atlassian/relay/use-fragment-naming, @atlassian/relay/use-fragment-naming
	const data = useFragment(
		graphql`
			fragment Nav4PlansForLanding on Query
			@argumentDefinitions(skipOrderQuery: { type: "Boolean!", defaultValue: false }) {
				...PlansList
				plansOrder: settings_navigationCustomisation(
					entityAri: $entityAriPlans
					ownerAri: $ownerAri
				) @skip(if: $skipOrderQuery) {
					...PlansList_navigationCustomisation
				}
			}
		`,
		// eslint-disable-next-line @atlassian/relay/use-fragment-naming
		queryRef,
	);

	const menuId = MENU_ID_PLANS;

	const handleExpansionToggle = useCallback(
		(hasExpanded: boolean) => {
			if (hasExpanded) {
				sendSectionItemAnalyticsEvent({
					menuId: L1_MENU_ID.PLANS,
					sectionItem: SECTION_ITEM_PLANS,
				});
			}
		},
		[sendSectionItemAnalyticsEvent],
	);

	return (
		<Nav4PlansForLandingInner
			{...props}
			formattedMessage={formattedMessage}
			handleExpansionToggle={handleExpansionToggle}
			icon={icon}
			menuId={menuId}
			queryRef={data}
		/>
	);
}

const Nav4PlansForLandingInner = componentWithFG(
	'navx-global-side-nav',
	Nav4PlansForLandingInnerGSN,
	Nav4PlansForLandingInnerNav4,
);

type InnerProps = Omit<Nav4PlansForLandingProps, 'queryRef'> & {
	formattedMessage: string;
	handleExpansionToggle: (hasExpanded: boolean) => void;
	icon: JSX.Element;
	menuId: string;
	queryRef: Nav4PlansForLanding$data;
};

function Nav4PlansForLandingInnerGSN(props: InnerProps) {
	const {
		actionsOnHover,
		draggableRef,
		dropIndicator,
		formattedMessage,
		handleExpansionToggle,
		hasDragIndicator,
		icon,
		isDragging,
		isExpanded,
		menuId,
		onExpandFlyoutToggle,
		queryRef,
		visualContentRef,
	} = props;

	const handleExpansionToggle2 = useCallback(
		(hasExpanded: boolean) => {
			handleExpansionToggle(hasExpanded);
			onExpandFlyoutToggle?.(hasExpanded);
		},
		[onExpandFlyoutToggle, handleExpansionToggle],
	);

	return (
		<Nav4ExpandableMenuItem
			dropTargetRef={visualContentRef}
			dropIndicator={dropIndicator}
			isDragging={isDragging}
			isExpanded={isExpanded}
			menuId={menuId}
			onExpansionToggle={handleExpansionToggle2}
		>
			<ExpandableMenuItemTrigger
				actionsOnHover={actionsOnHover}
				elemBefore={icon}
				hasDragIndicator={hasDragIndicator}
				interactionName="nav4-sidebar-plans-landing"
				isDragging={isDragging}
				ref={draggableRef}
				testId={getTestId(menuId)}
			>
				{formattedMessage}
			</ExpandableMenuItemTrigger>
			<ExpandableMenuItemContent>
				<PlansList
					fragmentRef={queryRef}
					navigationCustomisationFragment={queryRef.plansOrder ?? undefined}
				/>
				<Nav4PlansFooter />
			</ExpandableMenuItemContent>
		</Nav4ExpandableMenuItem>
	);
}

function Nav4PlansForLandingInnerNav4(props: InnerProps) {
	const {
		actionsOnHover,
		formattedMessage,
		handleExpansionToggle,
		icon,
		isExpanded,
		menuId,
		queryRef,
	} = props;

	return (
		<Nav4ExpandableMenuItem
			isExpanded={isExpanded}
			menuId={menuId}
			onExpansionToggle={handleExpansionToggle}
		>
			<ExpandableMenuItemTrigger
				interactionName="nav4-sidebar-plans-landing"
				actionsOnHover={actionsOnHover}
				elemBefore={icon}
				testId={getTestId(menuId)}
			>
				{formattedMessage}
			</ExpandableMenuItemTrigger>
			<ExpandableMenuItemContent>
				<PlansList
					fragmentRef={queryRef}
					navigationCustomisationFragment={queryRef.plansOrder ?? undefined}
				/>
				<Nav4PlansFooter />
			</ExpandableMenuItemContent>
		</Nav4ExpandableMenuItem>
	);
}
